/**
 * Domain Resolver Service
 * Detects and resolves organization slugs from various URL patterns
 */

import {
  extractSubdomain,
  extractOrgFromQuery,
  extractOrgFromPath,
  isValidDomain,
  sanitizeDomain,
  getCurrentUrl
} from '../utils/url-utils';

/**
 * DomainResolver class for detecting organization context
 */
class DomainResolver {
  constructor(config = {}) {
    this.config = {
      baseDomain: 'neurograph.io',
      queryParamName: 'org',
      pathPrefix: '/workbench/',
      customDomainMapping: {},
      ...config
    };
  }

  /**
   * Get current organization slug using multiple detection strategies
   * Priority order:
   * 1. Query parameter (?org=acme)
   * 2. Subdomain (acme.neurograph.io)
   * 3. Path-based (/workbench/acme)
   * 4. localStorage fallback
   * 5. null (default branding)
   *
   * @param {string} url - Optional URL to parse (defaults to current URL)
   * @returns {string|null} The organization slug or null
   */
  getCurrentSlug(url = null) {
    const targetUrl = url || getCurrentUrl();

    if (!targetUrl) {
      return this.getFromStorage();
    }

    // Strategy 1: Check query parameter
    const querySlug = extractOrgFromQuery(targetUrl, this.config.queryParamName);
    if (querySlug && isValidDomain(querySlug)) {
      const sanitized = sanitizeDomain(querySlug);
      this.saveToStorage(sanitized);
      return sanitized;
    }

    // Strategy 2: Check subdomain
    const subdomain = extractSubdomain(targetUrl, this.config.baseDomain);
    if (subdomain && isValidDomain(subdomain)) {
      const sanitized = sanitizeDomain(subdomain);
      this.saveToStorage(sanitized);
      return sanitized;
    }

    // Strategy 3: Check URL path
    const pathSlug = extractOrgFromPath(targetUrl, this.config.pathPrefix);
    if (pathSlug && isValidDomain(pathSlug)) {
      const sanitized = sanitizeDomain(pathSlug);
      this.saveToStorage(sanitized);
      return sanitized;
    }

    // Strategy 4: Check custom domain mapping
    const customSlug = this.resolveCustomDomain(targetUrl);
    if (customSlug) {
      this.saveToStorage(customSlug);
      return customSlug;
    }

    // Strategy 5: Fall back to localStorage
    return this.getFromStorage();
  }

  /**
   * Resolve custom domain to organization slug
   * Uses the customDomainMapping configuration
   *
   * @param {string} url - The URL to resolve
   * @returns {string|null} The organization slug or null
   */
  resolveCustomDomain(url) {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname;

      // Check if this hostname is in our custom domain mapping
      const slug = this.config.customDomainMapping[hostname];
      if (slug && isValidDomain(slug)) {
        return sanitizeDomain(slug);
      }

      return null;
    } catch (error) {
      console.error('Error resolving custom domain:', error);
      return null;
    }
  }

  /**
   * Validate a domain/slug format
   *
   * @param {string} domain - The domain to validate
   * @returns {boolean} True if valid, false otherwise
   */
  isValidDomain(domain) {
    return isValidDomain(domain);
  }

  /**
   * Get the current domain (full hostname)
   *
   * @returns {string} The current domain
   */
  getCurrentDomain() {
    const url = getCurrentUrl();
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch (error) {
      return '';
    }
  }

  /**
   * Map a custom domain to an organization slug
   *
   * @param {string} domain - The custom domain
   * @param {string} slug - The organization slug
   */
  mapCustomDomain(domain, slug) {
    if (isValidDomain(slug)) {
      this.config.customDomainMapping[domain] = sanitizeDomain(slug);
    }
  }

  /**
   * Save organization slug to localStorage
   *
   * @param {string} slug - The organization slug
   */
  saveToStorage(slug) {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem('neurograph:org-slug', slug);
      } catch (error) {
        console.warn('Failed to save slug to localStorage:', error);
      }
    }
  }

  /**
   * Get organization slug from localStorage
   *
   * @returns {string|null} The stored organization slug or null
   */
  getFromStorage() {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const stored = window.localStorage.getItem('neurograph:org-slug');
        return stored && isValidDomain(stored) ? stored : null;
      } catch (error) {
        console.warn('Failed to read slug from localStorage:', error);
      }
    }
    return null;
  }

  /**
   * Clear stored organization slug
   */
  clearStorage() {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.removeItem('neurograph:org-slug');
      } catch (error) {
        console.warn('Failed to clear slug from localStorage:', error);
      }
    }
  }
}

// Export singleton instance
export default new DomainResolver();

// Export class for custom instances
export { DomainResolver };
